import { MapPin, Trash2 } from 'lucide-react'
import { useMemo } from 'react'

import { MultiSelectAutocomplete, type SelectOption } from '@/components/ui/MultiSelectAutocomplete'
import { type Location } from '@/models/domain/location'

interface LocationsTableProps {
  locations: Location[]
  selectedLocationIds: string[]
  onChange: (locationIds: string[]) => void
  disabled?: boolean
  isLoading?: boolean
  locationsAll?: boolean
}

function getSearchPlaceholder(locationsAll: boolean, availableCount: number): string {
  if (locationsAll) return 'Access granted to all locations'
  if (availableCount === 0) return 'All available locations already added'
  return 'Search and add locations...'
}

function pluralSuffix(count: number): string {
  return count !== 1 ? 's' : ''
}

function LocationsBody({
  selectedLocations,
  selectedLocationIds,
  availableLocationOptions,
  onChange,
  disabled,
  locationsAll,
}: {
  selectedLocations: Location[]
  selectedLocationIds: string[]
  availableLocationOptions: SelectOption[]
  onChange: (locationIds: string[]) => void
  disabled: boolean
  locationsAll: boolean
}) {
  const handleAddLocations = (newLocationIds: string[]) => {
    onChange([...selectedLocationIds, ...newLocationIds])
  }

  const handleRemoveLocation = (locationId: string) => {
    if (disabled || locationsAll) return
    onChange(selectedLocationIds.filter((id) => id !== locationId))
  }

  const isSearchDisabled = disabled || locationsAll || availableLocationOptions.length === 0

  return (
    <div className="space-y-3">
      <LocationSearch
        options={availableLocationOptions}
        onAdd={handleAddLocations}
        disabled={isSearchDisabled}
        isLoading={false}
        placeholder={getSearchPlaceholder(locationsAll, availableLocationOptions.length)}
      />

      {selectedLocations.length > 0 && (
        <SelectedLocationsGrid
          locations={selectedLocations}
          onRemove={handleRemoveLocation}
          disabled={disabled || locationsAll}
        />
      )}

      {selectedLocations.length === 0 && !locationsAll && <NoLocationsAssigned />}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {selectedLocations.length} location{pluralSuffix(selectedLocations.length)} assigned
      </p>
    </div>
  )
}

export function LocationsTable({
  locations,
  selectedLocationIds,
  onChange,
  disabled = false,
  isLoading = false,
  locationsAll = false,
}: LocationsTableProps) {
  const selectedLocations = useMemo(() => {
    return selectedLocationIds
      .map((id) => locations.find((l) => String(l.id) === id))
      .filter((l): l is Location => l !== undefined)
      .sort((a, b) => (a.name || `Location ${a.id}`).localeCompare(b.name || `Location ${b.id}`))
  }, [locations, selectedLocationIds])

  const availableLocationOptions: SelectOption[] = useMemo(() => {
    return locations
      .filter((l) => l.active && !selectedLocationIds.includes(String(l.id)))
      .map((l) => ({ id: String(l.id), name: l.name || `Location ${l.id}` }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [locations, selectedLocationIds])

  if (isLoading) return <LocationsLoadingSkeleton />
  if (locations.length === 0) return <LocationsEmptyState />

  return (
    <LocationsBody
      selectedLocations={selectedLocations}
      selectedLocationIds={selectedLocationIds}
      availableLocationOptions={availableLocationOptions}
      onChange={onChange}
      disabled={disabled}
      locationsAll={locationsAll}
    />
  )
}

const LocationsLoadingSkeleton = () => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
    <div className="p-4 space-y-3">
      {[...Array<undefined>(3)].map((_, i) => (
        <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      ))}
    </div>
  </div>
)

const LocationsEmptyState = () => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
    <MapPin className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-3" />
    <p className="text-sm text-gray-500 dark:text-gray-400">No locations available</p>
  </div>
)

const NoLocationsAssigned = () => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
    <MapPin className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-600 mb-2" />
    <p className="text-sm text-gray-500 dark:text-gray-400">
      No locations assigned. Use the search above to add locations.
    </p>
  </div>
)

const LocationSearch = ({
  options,
  onAdd,
  disabled,
  isLoading,
  placeholder,
}: {
  options: SelectOption[]
  onAdd: (ids: string[]) => void
  disabled: boolean
  isLoading: boolean
  placeholder: string
}) => (
  <div className="space-y-2">
    <label
      htmlFor="add-locations-search"
      className="text-sm font-medium text-gray-700 dark:text-gray-300"
    >
      Add locations
    </label>
    <MultiSelectAutocomplete
      id="add-locations-search"
      options={options}
      value={[]}
      onChange={onAdd}
      placeholder={placeholder}
      disabled={disabled}
      isLoading={isLoading}
    />
  </div>
)

const TH_CLASS =
  'px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'

const SelectedLocationsGrid = ({
  locations,
  onRemove,
  disabled,
}: {
  locations: Location[]
  onRemove: (id: string) => void
  disabled: boolean
}) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th className={TH_CLASS}>Location Name</th>
            <th className={TH_CLASS}>City</th>
            <th className={TH_CLASS}>State</th>
            <th className={TH_CLASS}>ID</th>
            <th className={`w-20 ${TH_CLASS}`}>Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {locations.map((location) => (
            <tr
              key={location.id}
              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                {location.name || `Location ${location.id}`}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                {location.city || '-'}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                {location.state || '-'}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-mono">
                {location.id}
              </td>
              <td className="px-4 py-3">
                <button
                  type="button"
                  onClick={() => {
                    onRemove(String(location.id))
                  }}
                  disabled={disabled}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Remove location"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)
